import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { _ as _object_without_properties } from "@swc/helpers/_/_object_without_properties";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import Decimal from 'decimal.js';
import { Controller, useForm } from 'react-hook-form';
import { getRequiredValidator, richTextValidation, titleValidation } from 'validators';
import { BiContainer, Button, Callout, CustomModal, TableList, TextField, Typography, useOverlayContext } from 'seagull';
import { useLocaleKeys } from '~/i18n';
import { handlePromise, sendErrReport } from '~/infra/utils';
import { MoneyModel } from '~/models';
import { useBillingObservable } from '~/providers/BillingObservableProvider';
import { useStoreActions } from '~/store/store-context';
import { helpArticles } from '~/utils/helpArticles';
import { ServiceTemplateInput } from '../common/ServiceTemplateInput/ServiceTemplateInput';
import ExternalLinkButton from '../ExternalLinkButton';
import styles from './AddCreditModal.module.scss';
import { useAddCredit } from './hooks/useAddCredit';
const testPrefix = 'add-credit-modal';
const headerItems = [
    {
        value: 'Service name'
    },
    {
        value: 'Total'
    }
];
const columnDefs = [
    {
        width: 450
    },
    {
        width: 125
    }
];
const getShouldShowFieldError = (fieldState, formState)=>fieldState.error && (fieldState.isTouched || formState.isSubmitted);
export default function AddCreditModal(param) {
    let { dismissModal, relationship, nextBillingDate } = param;
    const { t: texts } = useLocaleKeys('components.addCreditModal');
    const { addCredit } = useAddCredit();
    const { control, formState, handleSubmit, setValue } = useForm({
        mode: 'all',
        defaultValues: {
            note: '',
            price: null
        }
    });
    const overlayContext = useOverlayContext();
    const { logBI } = useStoreActions();
    const { notifyToRefresh } = useBillingObservable();
    const applyCredit = async (param)=>{
        let { name, note, price, serviceTemplateId } = param;
        var _ref;
        if (!nextBillingDate) return;
        logBI(18001, {
            title: name,
            note,
            price: price === null || price === void 0 ? void 0 : price.toNumber()
        });
        const loaderName = 'addCreditLoader';
        overlayContext.startLoader(loaderName);
        const rate = (_ref = price === null || price === void 0 ? void 0 : price.amount()) !== null && _ref !== void 0 ? _ref : new Decimal(0);
        const result = await handlePromise(addCredit({
            data: {
                amount: rate.toString(),
                note,
                serviceTemplateId
            },
            relationshipId: relationship.id
        }));
        if (!result.success) {
            overlayContext.showErrorToast('Failed to add credit due to an unexpected error', "".concat(testPrefix, "-error-toast"));
            sendErrReport({
                type: 'chargeError',
                error: result.error
            });
        } else {
            overlayContext.showSuccessToast("".concat(price ? price.abs().display() : '', " credit was added to ").concat(relationship.customer.name), "".concat(testPrefix, "-success-toast"));
            notifyToRefresh();
            dismissModal({
                status: 'success'
            });
        }
        overlayContext.stopLoader(loaderName);
    };
    return /*#__PURE__*/ _jsx(BiContainer, {
        name: "add_credit_modal",
        data: {
            relationshipId: relationship.id,
            clientId: relationship.customer.id
        },
        children: /*#__PURE__*/ _jsx(CustomModal, {
            className: styles.modal,
            header: /*#__PURE__*/ _jsx("div", {
                "data-testid": "".concat(testPrefix, "-header"),
                className: styles.header,
                children: /*#__PURE__*/ _jsx(Typography, {
                    variant: "h6",
                    weight: "bold",
                    children: texts.title()
                })
            }),
            footer: /*#__PURE__*/ _jsx("div", {
                className: styles.actions,
                children: /*#__PURE__*/ _jsx(Button, {
                    type: "submit",
                    testId: "".concat(testPrefix, "-add-credit-button"),
                    bi: {
                        name: 'add_credit'
                    },
                    loading: formState.isSubmitting,
                    form: testPrefix,
                    children: "Add credit"
                })
            }),
            width: "784px",
            height: "524px",
            testId: testPrefix,
            onClose: ()=>dismissModal({
                    status: 'canceled'
                }),
            headerClassName: styles.blueHeader,
            children: /*#__PURE__*/ _jsxs("form", {
                onSubmit: handleSubmit(applyCredit),
                id: testPrefix,
                className: styles.content,
                children: [
                    /*#__PURE__*/ _jsxs(Callout, {
                        children: [
                            "Credit added goes towards the associated agreement and gets applied automatically to future invoices.",
                            ' ',
                            /*#__PURE__*/ _jsx(ExternalLinkButton, {
                                openInNewTab: true,
                                underline: "none",
                                href: helpArticles.applyClientCredit(),
                                children: "Learn more about how credits work"
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: styles.formFields,
                        children: [
                            /*#__PURE__*/ _jsx("div", {
                                children: /*#__PURE__*/ _jsxs(TableList.Provider, {
                                    columnDefs: columnDefs,
                                    children: [
                                        /*#__PURE__*/ _jsx(TableList.Header, {
                                            items: headerItems
                                        }),
                                        /*#__PURE__*/ _jsx(TableList.Row, {
                                            items: [
                                                {
                                                    value: /*#__PURE__*/ _jsx(Controller, {
                                                        control: control,
                                                        name: "serviceTemplateId",
                                                        rules: titleValidation('', {
                                                            required: true
                                                        }),
                                                        render: (renderProps)=>/*#__PURE__*/ _jsx(ServiceTemplateInput, {
                                                                relationship: relationship,
                                                                renderProps: renderProps,
                                                                testId: "".concat(testPrefix, "-dropdown-service-selector"),
                                                                applyServiceChange: (serviceTemplate)=>{
                                                                    var _ref;
                                                                    setValue('name', (_ref = serviceTemplate === null || serviceTemplate === void 0 ? void 0 : serviceTemplate.name) !== null && _ref !== void 0 ? _ref : '');
                                                                }
                                                            })
                                                    })
                                                },
                                                {
                                                    value: /*#__PURE__*/ _jsx(Controller, {
                                                        control: control,
                                                        name: "price",
                                                        rules: {
                                                            validate: {
                                                                required: getRequiredValidator(),
                                                                positiveAmount: (value)=>!!value && value.amount().gt(0)
                                                            }
                                                        },
                                                        render: (param)=>{
                                                            let { field: { value, ref, onBlur, onChange }, fieldState, formState } = param;
                                                            return /*#__PURE__*/ _jsx(TextField, {
                                                                collapseHelperText: false,
                                                                isMoney: true,
                                                                size: "small",
                                                                prefix: "$",
                                                                placeholder: "0.00",
                                                                value: value != null ? value.toFixed() : '',
                                                                inputRef: ref,
                                                                onBlur: onBlur,
                                                                testId: "".concat(testPrefix, "-cost-input"),
                                                                bi: {
                                                                    name: 'credit_amount'
                                                                },
                                                                error: getShouldShowFieldError(fieldState, formState),
                                                                onChange: (e)=>{
                                                                    const dollarsAsString = e.currentTarget.value;
                                                                    const newValue = dollarsAsString ? new MoneyModel(new Decimal(dollarsAsString)) : null;
                                                                    onChange(newValue);
                                                                }
                                                            });
                                                        }
                                                    })
                                                }
                                            ]
                                        })
                                    ]
                                })
                            }),
                            /*#__PURE__*/ _jsx(Controller, {
                                control: control,
                                name: "note",
                                rules: richTextValidation('Note'),
                                render: (_0)=>{
                                    let { field: _ref } = _0, { ref } = _ref, restField = _object_without_properties(_ref, [
                                        "ref"
                                    ]), { fieldState } = _0;
                                    return /*#__PURE__*/ _jsx(TextField, _object_spread_props(_object_spread({
                                        collapseHelperText: false
                                    }, restField), {
                                        testId: "".concat(testPrefix, "-note-input"),
                                        bi: {
                                            name: 'credit_note'
                                        },
                                        label: texts.noteLabel(),
                                        multiline: true,
                                        minRows: 3,
                                        maxRows: 3,
                                        inputRef: ref,
                                        error: getShouldShowFieldError(fieldState, formState),
                                        helperText: getShouldShowFieldError(fieldState, formState) && fieldState.error.message
                                    }));
                                }
                            })
                        ]
                    })
                ]
            })
        })
    });
}
